import {
  AllocationsTableData,
  AllocationTableRow,
  getAllocationTableData,
} from "@client/components/round/allocator/AllocationsView";
import { getRatingRows } from "@client/components/round/allocator/RatingTable/RatingTable";
import { PageTitle } from "@client/components/round/PageTitle";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import LoadingSpinner from "@client/components/utils/LoadingSpinner";
import { DownloadFile } from "@client/utils/DownloadFile";
import { exportScores } from "@client/utils/exportScores";
import userIsAdmin from "@client/utils/userIsAdmin";
import {
  getActiveEvaluatorEvaluations,
  getActiveFunders,
  getActiveRecommenders,
  getAllProposalForms,
  getEvaluatorTrancheList,
  isUserFunder,
} from "@engine/round/accessors";
import type {
  Evaluation,
  Proposal,
  ProposalForm,
  RoundAggregate,
} from "@engine/round/aggregate";
import { Node } from "@engine/round/aggregate";
import { getMeanRecommendation } from "@engine/round/updateMeanRecommender";
import {
  runSProcessOnRound,
  type SProcessOutput,
} from "@lib/algoInterface/runSProcessOnRound";
import { meanUserId } from "@lib/constants";
import { deepCopy } from "@lib/deepCopy";
import partition from "lodash/partition";
import range from "lodash/range";
import sum from "lodash/sum";
import { useContext } from "react";
import { Redirect } from "wouter";
import { StateContext } from "./Round";
import {
  convertGrantAmountToNumber,
  useSpeculationGrantData,
  type SpeculationGrantData,
} from "./useSpeculationGrantData";

export const ExportData = withErrorBoundary(() => {
  const context = useContext(StateContext);
  const { round, actingAsUserId: userId } = context;
  const loader = useSpeculationGrantData();
  const recommenders = getActiveRecommenders(round);
  const funders = getActiveFunders(round);

  const isAdmin = userIsAdmin(userId, context);
  const isFunder = isUserFunder(round, userId);
  if (!isAdmin && !isFunder) {
    return <Redirect to="/" />;
  }

  if (loader.type === "loading") {
    return <LoadingSpinner />;
  }

  if (loader.type === "error") {
    return `Error: ${loader.error.message}`;
  }

  const sProcessResults = runSProcessOnRound(round);
  const recommenderAllocations = getAllocationTableData(
    sProcessResults,
    "accountables",
    "recommenders/proposals",
  );

  // Only use speculation grants data if it's available
  const hasSpeculationGrantsData = loader.type === "success";
  const speculationGrantsData = hasSpeculationGrantsData ? loader.data : null;

  // Create a safe empty object for completeResultsExport in case of null
  const safeSpeculationGrantsData = speculationGrantsData || {
    applicationSpeculationGrantData: {},
    totalGrantAmount: 0,
    settlementBudget: 0,
    funderId: "",
    speculationSettlements: "",
  };

  const buttonClassName =
    "p-2 rounded bg-stone-400 hover:bg-stone-500 dark:bg-stone-700 dark:hover:bg-stone-800 mt-2";

  return (
    <div className="p-4">
      <PageTitle title="Export Data" />
      <div className="flex py-2">
        <DownloadFile
          data={dbExport(round)}
          filename={`${round.name}_database.json`}
          className={buttonClassName}
          title="Database Export"
        />
      </div>
      <div className="flex py-2">
        <DownloadFile
          data={finalAllocationsExport(recommenderAllocations)}
          filename={`${round.name}_final_allocations.tsv`}
          className={buttonClassName}
          title="Allocation TSV Export"
        />
      </div>
      <div className="flex py-2">
        <DownloadFile
          data={preciseFinalAllocationsExport(recommenderAllocations)}
          filename={`${round.name}_precise_final_allocations.tsv`}
          className={buttonClassName}
          title="Precise Allocation TSV Export"
        />
      </div>
      <div className="flex py-2">
        <DownloadFile
          data={completeResultsExport(
            round,
            recommenderAllocations,
            safeSpeculationGrantsData,
          )}
          filename={`${round.name}_final_output.tsv`}
          className={buttonClassName}
          title="Final Output TSV Export"
        />
      </div>
      <div className="flex py-2">
        <DownloadFile
          data={recommenderAllocationsExport(round, recommenderAllocations)}
          filename={`${round.name}_recommender_allocations.tsv`}
          className={buttonClassName}
          title="Recommender Allocation TSV Export"
        />
      </div>
      <div className="flex py-2">
        <DownloadFile
          data={funderAllocationsExport(round, sProcessResults)}
          filename={`${round.name}_funder_allocations.tsv`}
          className={buttonClassName}
          title="Funder Allocation TSV Export"
        />
      </div>
      {funders.map((funder) => (
        <div className="flex py-2" key={funder.id}>
          <DownloadFile
            data={exportScores(
              getRatingRows(round, funder),
              recommenders
                .map((r) => r.shortname)
                .filter((r) => !r.startsWith("Mean"))
                .sort(),
            )}
            filename={`${round.name}_${funder.shortname}_scores.tsv`}
            className={buttonClassName}
            title={`Export ${funder.shortname} Scores`}
          />
        </div>
      ))}
      {/* Only show the Speculation Settlements export button if we have the data */}
      {speculationGrantsData && (
        <div className="flex py-2">
          <DownloadFile
            data={speculationGrantsData.speculationSettlements}
            filename={`${round.name}_speculation_settlements.tsv`}
            className={buttonClassName}
            title="Export Speculation Settlements"
          />
        </div>
      )}
    </div>
  );
});

type DBData = {
  round: RoundAggregate;
  proposalForms: ProposalForm[];
};

function dbExport(round: RoundAggregate): DBData {
  const exportRound = deepCopy(round);

  // Data for db export
  const proposalForms = getAllProposalForms(round);

  const [regularRecommenders, meanRecommenders] = partition(
    getActiveRecommenders(exportRound),
    (recommender) => recommender?.meanRecommenderThreshold == null,
  );

  for (const recommender of regularRecommenders) {
    recommender.trancheList = getEvaluatorTrancheList(round, recommender);
  }

  for (const meanRecommender of meanRecommenders) {
    const meanEvaluationsList = getActiveEvaluatorEvaluations(
      round,
      meanRecommender,
    );

    const meanEvaluations: Record<string, Evaluation> = {};
    for (const evaluation of meanEvaluationsList) {
      meanEvaluations[evaluation.evaluableId] = evaluation;
    }

    const meanTrancheList = getMeanRecommendation(
      round,
      meanRecommender.meanRecommenderThreshold!,
    );

    exportRound.members[meanUserId].evaluators[meanRecommender.id].trancheList =
      meanTrancheList;

    exportRound.members[meanUserId].evaluators[meanRecommender.id].evaluations =
      meanEvaluations;
  }

  return {
    round: exportRound,
    proposalForms,
  };
}

function recommenderAllocationsExport(
  round: RoundAggregate,
  allocationData: AllocationsTableData,
): string {
  const sortedRecommenders = allocationData.evaluators.sort((a, b) =>
    a.shortname.localeCompare(b.shortname),
  );

  const recommenderHeaders = sortedRecommenders.map(
    (recommender) => recommender.shortname,
  );

  const header = ["app_id", "org_name", ...recommenderHeaders].join("\t");

  const rowData: string[] = [];
  for (const proposalForm of Object.values(round.proposalForms)) {
    for (const row of allocationData.rows) {
      const proposal = proposalForm.proposals[row.evaluable.id];
      if (!proposal || sum(Object.values(row.allocations)) === 0) {
        continue;
      }

      const recommenderAllocations = sortedRecommenders.map((recommender) => {
        const allocation = row.allocations[recommender.id];
        return allocation ? Math.round(allocation) : "";
      });

      rowData.push(
        [proposal.shortname, proposal.longname, ...recommenderAllocations].join(
          "\t",
        ),
      );
    }
  }

  return [header, ...rowData].join("\n");
}

function funderAllocationsExport(
  round: RoundAggregate,
  sProcessResults: SProcessOutput,
): string {
  const funders = sProcessResults.parentNodes;
  const numFunders = funders.length;
  const numRecommenders = sProcessResults.layerNodes[0].length - 1; // -1 because not counting Hold
  const numOrgs = sProcessResults.layerNodes[1].length;
  const totalSpentByFunder: Record<string, number> = {};
  const totalSpentByRecommender: Record<string, number> = {};
  const recommenderNamesList: string[] = [];

  const funderToOrg: number[][] = [];
  for (let i = 0; i < numFunders; i++) {
    let row: number[] = [];
    for (let j = 0; j < numOrgs; j++) row.push(0);
    funderToOrg.push(row);
  }

  for (
    let recommenderIndex = 0;
    recommenderIndex < numRecommenders;
    recommenderIndex++
  ) {
    const recommender = sProcessResults.layerNodes[0][recommenderIndex + 1];
    recommenderNamesList.push(recommender.shortname);
    totalSpentByRecommender[recommender.id] = 0;
  }
  for (let funderIndex = 0; funderIndex < numFunders; funderIndex++) {
    const funder = sProcessResults.parentNodes[funderIndex];
    const funderId = funder.id;
    totalSpentByFunder[funderId] = 0;

    for (
      let recommenderIndex = 0;
      recommenderIndex < numRecommenders;
      recommenderIndex++
    ) {
      const recommender = sProcessResults.layerNodes[0][recommenderIndex + 1];
      const funderToRecommender = sProcessResults.layerAccounts[0].get(
        funderIndex,
        recommenderIndex + 1,
      );
      const totalPassingThroughRecommender = sProcessResults.layerAccounts[0]
        .getCol(recommenderIndex + 1)
        .sum();
      if (totalPassingThroughRecommender === 0) {
        continue;
      }
      totalSpentByRecommender[recommender.id] += totalPassingThroughRecommender;
      totalSpentByFunder[funderId] += totalPassingThroughRecommender;

      for (let orgIndex = 0; orgIndex < numOrgs; orgIndex++) {
        const recommenderToOrg = sProcessResults.layerAccounts[1].get(
          recommenderIndex,
          orgIndex,
        );
        funderToOrg[funderIndex][orgIndex] +=
          funderToRecommender *
          (recommenderToOrg / totalPassingThroughRecommender);
      }
    }
  }

  const rows = range(0, numOrgs)
    .map((orgIndex) => {
      const orgNode = sProcessResults.layerNodes[1][orgIndex];
      const orgShortname = orgNode.shortname;
      const amounts = range(0, numFunders)
        .map((funderIndex) => {
          const amount = funderToOrg[funderIndex][orgIndex];
          return Math.round(amount / 1000) * 1000;
        })
        .join("\t");
      const totalAmount =
        Math.round(
          range(0, numFunders)
            .map((funderIndex) => funderToOrg[funderIndex][orgIndex])
            .reduce((sum, n) => sum + n, 0) / 1000,
        ) * 1000;

      const receivingCharity = "TODO";
      const purpose = "TODO";

      if (totalAmount === 0) {
        return null;
      }

      return [
        orgShortname,
        amounts,
        totalAmount,
        receivingCharity,
        purpose,
      ].join("\t");
    })
    .filter((row) => row !== null);

  console.log(
    `Recommenders (${numRecommenders}): ${recommenderNamesList.join(", ")}`,
  );
  for (const member of Object.values(round.members)) {
    if (member.status !== "active") continue;
    for (const evaluator of Object.values(member.evaluators)) {
      if (evaluator.type === "funder" && evaluator.status === "active") {
        console.log(
          `Funder ${evaluator.shortname}: Budget ${evaluator.budget}, spent ${totalSpentByFunder[evaluator.id]}`,
        );
      }
    }
  }
  for (const member of Object.values(round.members)) {
    if (member.status !== "active") continue;
    for (const evaluator of Object.values(member.evaluators)) {
      if (evaluator.type === "recommender" && evaluator.status === "active") {
        console.log(
          `Recommender ${evaluator.shortname} (${evaluator.id}): Spent ${totalSpentByRecommender[evaluator.id] ?? 0}`,
        );
      }
    }
  }

  const header = [
    "Organization",
    ...funders.map((f) => `Amount (${f.shortname})`),
    "Total Amount",
    "Receiving Charity",
    "Purpose",
  ].join("\t");

  return [header, ...rows].join("\n");
}

function finalAllocationsExport(recommenderAllocations: AllocationsTableData) {
  const header = ["Org Name", "Grant Amount"].join("\t");
  const rowData = recommenderAllocations.rows.map((row) =>
    [
      row.evaluable.shortname,
      String(Math.round(row.evaluableTotal / 1000) * 1000),
    ].join("\t"),
  );

  return [header, ...rowData].join("\n");
}

function preciseFinalAllocationsExport(
  recommenderAllocations: AllocationsTableData,
): string {
  const header = ["Org Name", "Grant Amount"].join("\t");

  const rows = recommenderAllocations.rows.sort((a, b) =>
    a.evaluable.shortname
      .toLowerCase()
      .localeCompare(b.evaluable.shortname.toLowerCase()),
  );

  const rowData = rows.map((row) =>
    [row.evaluable.shortname, Math.round(row.evaluableTotal)].join("\t"),
  );

  return [header, ...rowData].join("\n");
}

function completeResultsExport(
  round: RoundAggregate,
  recommenderAllocations: AllocationsTableData,
  speculationGrantsData: SpeculationGrantData,
) {
  const sortedRecommenders = recommenderAllocations.evaluators.sort((a, b) =>
    a.shortname.localeCompare(b.shortname),
  );

  const recommenderHeaders = sortedRecommenders.map(
    (recommender) => recommender.shortname,
  );

  const headers = [
    "app_id",
    "org_name",
    "usd_amount",
    "receiving_org",
    "country",
    "tax_id",
    "purpose",
    "contact_name",
    "contact_email",
    "speculation_grant",
    "comment",
    "recommender_allocations:",
    ...recommenderHeaders,
  ].join("\t");

  const sortedAllocationTableRows = recommenderAllocations.rows.sort((a, b) => {
    const aProposal = a.evaluable as Proposal;
    const bProposal = b.evaluable as Proposal;
    const aName = aProposal?.longname || a.evaluable.shortname;
    const bName = bProposal?.longname || b.evaluable.shortname;

    return aName.toLowerCase().localeCompare(bName.toLowerCase());
  });

  const exportRows = sortedAllocationTableRows
    .filter((row) => row.evaluable.id !== round.holdProposalId)
    .map((allocationTableRow) =>
      getAllocationRow(
        round,
        allocationTableRow,
        speculationGrantsData,
        sortedRecommenders,
      ),
    );

  return [headers, ...exportRows].join("\n");
}

function getAllocationRow(
  round: RoundAggregate,
  allocationTableRow: AllocationTableRow,
  speculationGrantsData: SpeculationGrantData,
  sortedRecommenders: Node[],
): string {
  const proposal = allocationTableRow.evaluable as Proposal;

  const proposalForm: ProposalForm | null =
    Object.values(round.proposalForms).find(
      (proposalForm) => proposal.id in proposalForm.proposals,
    ) ?? null;

  const getValue = (title: string) =>
    getProposalFormFieldValue(proposalForm, proposal, title);

  const sffApplicationId = getValue("SFF Application ID");

  const speculationGrant = (function () {
    if (sffApplicationId.startsWith("ERROR")) {
      return "ERROR: failed to find SFF Application ID";
    }

    const grantData =
      speculationGrantsData.applicationSpeculationGrantData[sffApplicationId];

    if (!grantData) {
      return 0;
    }

    const grantRows = grantData.simultaneousGrants.flatMap(
      (simuultaneousGrant) => simuultaneousGrant.grants,
    );

    const totalGrantAmount = grantRows.reduce(
      (acc, grantRow) =>
        acc + convertGrantAmountToNumber(grantRow["Speculator Amount"]),
      0,
    );

    return Math.round(totalGrantAmount / 1000) * 1000;
  })();

  const recommenderAllocations = sortedRecommenders.map((recommender) => {
    const allocation = allocationTableRow.allocations[recommender.id];
    return allocation ? Math.round(allocation / 1000) * 1000 : "";
  });

  return [
    sffApplicationId,
    getValue("Organization name"),
    Math.round(allocationTableRow.evaluableTotal / 1000) * 1000,
    getValue("Name of receiving charity"),
    getValue("Country of the receiving charity"),
    getValue("Tax ID of the receiving charity"),
    "", // purpose
    getValue("Contact person"),
    getValue("Contact email"),
    speculationGrant,
    "", // comment
    "", // recommender allocations:
    ...recommenderAllocations,
  ].join("\t");
}

function getProposalFormFieldValue(
  proposalForm: ProposalForm | null,
  proposal: Proposal | null,
  title: string,
): string {
  if (!proposalForm) {
    return "ERROR: Missing proposal form";
  }

  const field = proposalForm.fields.find((field) => field.title === title);

  if (!field) {
    return "ERROR: Missing proposal form field";
  }

  return getProposalFormValueById(proposalForm, proposal, field.id);
}

function getProposalFormValueById(
  proposalForm: ProposalForm | null,
  proposal: Proposal | null,
  fieldId: string,
): string {
  if (!proposalForm) {
    return "ERROR: Missing proposal form";
  }

  if (!proposal) {
    return "ERROR: Missing proposal";
  }

  const response = proposal.fieldResponses[fieldId];
  if (!response) {
    return "ERROR: Missing form response";
  }

  if (response.type === "error") {
    return "ERROR: Invalid form response";
  }

  return response.value;
}
